import React, { useState, useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import { useInput } from "@lib-shared/hooks";
import useFileManager from "core/hooks/use-file-manager";
import { convertToHtml, imageUrl, logApiError } from "core/lib";
import AddAttachment from "./add-attachment";
import Attachments from "./attachments";
import { ShowModal, ModalAlert, ModalIcon, ValidationMessage } from "components/common-ui";

type NewCommentFormProps = {
  auth: any,
  display: boolean,
  onSubmit: any,
  onClose: any,
}

export const NewCommentForm = (props: NewCommentFormProps) => {
  const { auth, display, onSubmit, onClose } = props;
  const [isGlobalOversize, setIsGlobalOversize] = useState(false);
  const [isNotSupported, setIsNotSupported] = useState(() => false);
  const [isPosting, setIsPosting] = useState(false);
  // const [filePerc, setFilePerc] = useState();

  let existingDialog: any = null;

  const closeModal = () => {
    if (existingDialog != null) {
      ModalAlert.close(existingDialog);
    }
  }

  const showModal = (text: string, isError = false, showButtons = false, title = "Comment") => {
    closeModal();

    const options = {
      title: title,
      body: text,
      showButton: showButtons,
      showClose: false,
      icon: isError ? ModalIcon.error : ModalIcon.tick
    };

    existingDialog = ShowModal(options);
  }

  //file manager
  const {
    handleAttachmentChange,
    uploadedFiles,
    reloadAttachment,
    removeAttachmentHandler,
    resetUploadedFiles,
  } = useFileManager([]);

  useEffect(() => {
    const isNotSupported = uploadedFiles.filter((file: any) => file.notSupported);
    const isOversize = uploadedFiles.filter((file: any) => file.overSize);

    setIsNotSupported(isNotSupported.length > 0);
    setIsGlobalOversize(isOversize.length > 0);
  }, [uploadedFiles]);

  //validations
  const {
    value: newComment,
    isValid: newCommentIsValid,
    hasError: newCommentInputHasError,
    valueChangeHandler: newCommentChangedHandler,
    inputBlurHandler: newCommentBlurHandler,
    reset: resetNewCommentInput,
  } = useInput((value: any) => !isEmpty(value));

  const formIsValid = (newCommentIsValid && !isGlobalOversize && !isNotSupported);

  const resetForm = (cssClassName: string) => {
    const form = document.querySelector(`.${cssClassName}`) as HTMLFormElement;
    form?.reset();
  }

  const resetAll = () => {
    resetUploadedFiles();
    resetNewCommentInput();

    resetForm("new-comment");
    resetForm("mobile");
  }

  const close = (e: any) => {
    onClose();

    resetAll();
  }

  const submitAsync = async (e: any) => {
    e.preventDefault();

    if (!formIsValid) {
      return;
    }

    setIsPosting(true);

    showModal("Your comment is being submitted", false, false);

    const attachments = uploadedFiles.filter((file: any) => file.file);

    const comment = {
      user_id: auth.contactId,
      body: convertToHtml(newComment),
      attachments: attachments
    };

    const handleFailure = () => showModal("Sorry something went wrong while submitting your comment.", true, true);

    try {
      const result = await onSubmit(comment);

      setIsPosting(false);

      if (result !== true) {
        handleFailure();
        return;
      }

      showModal("Your comment has been submitted", false, true);

      resetAll();

      setTimeout(closeModal, 3000);
    } catch (error) {
      logApiError(error);

      handleFailure();
    };
  }

  if (!display) {
    return null;
  }

  const planeImage = imageUrl("plane.svg");

  const attachmentsCssName = "attachments-container" + (uploadedFiles.length === 0 ? " none" : "");

  return (
    <>
      <div className={attachmentsCssName}>
        <Attachments
          uploadedFiles={uploadedFiles}
          removeAttachmentHandler={removeAttachmentHandler}
          reloadAttachment={reloadAttachment}
          handleAttachmentChange={handleAttachmentChange}
          showAddButton={false} />
      </div>

      <form
        className="new-comment"
        onSubmit={async (e) => await submitAsync(e)}>

        <textarea
          //placeholder="What would you like to add?"
          id="comment"
          name="comment"
          rows={4}
          value={newComment}
          // onInput={autoTextBoxHeight}
          onChange={newCommentChangedHandler}
          onBlur={newCommentBlurHandler}
        />

        <ValidationMessage
          valid={!newCommentInputHasError}
          message="Please enter your comment"
        />

        <AddAttachment
          handleAttachmentChange={(e: any) => handleAttachmentChange(e.currentTarget.files)}
          showMessage={false} />

        <div className="row modal-buttons-row">
          <button type="button" className="btn btn-outline-black"
            onClick={close}>Back</button>
          <button
            type="submit"
            disabled={!formIsValid || isPosting}
            className={`${formIsValid && !isPosting ? "" : "disabled"
              } btn btn-black`}
          >
            Submit
          </button>
        </div>
      </form>

      <form className="mobile">
        <AddAttachment
          handleAttachmentChange={(e: any) => handleAttachmentChange(e.currentTarget.files)}
          isMobile={true}
          showMessage={false} />

        <textarea
          placeholder="Enter your message here"
          id="comment"
          name="comment"
          rows={1}
          value={newComment}
          // onInput={autoTextBoxHeight}
          onChange={newCommentChangedHandler}
          onBlur={newCommentBlurHandler}
        />

        <button type="button" title="Submit">
          <img src={planeImage} alt="Submit" />
        </button>
      </form>
    </>
  );
}

export default NewCommentForm;
